import { WizardDraftsBar } from "@/shared/wizardDraft/WizardDraftsBar";   // WIZDRAFT-2
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Can } from "@/auth/permissions";
import { Plus, Search, ArrowRight, Briefcase, Wallet, Settings2 } from "lucide-react";
import {
  partnersApi,
  ACTIVITY_LABEL,
  tierChip,
  tierAvatar,
  type PartnerActivity,
  type BoardRow,
} from "@/services/b2b";
import { PageShell, LoadingState, EmptyState } from "@/shared/ui/PageHeader";
import { formatCents } from "@/services/finance";

/**
 * 同业 B2B · 客户管理 — 对齐 sign-off canvas「同业 B2B - 客户列表」(铁律十九)。
 * 健康度看板式列表：4 KPI + 动态分级 baseline 说明卡 + 分层 tab + 客户行。
 * 分级动态可配（partner_tiers）· 活跃度从最近订单+status 派生（后端 _board）。
 */
type SortKey = "health" | "volume";

const ACT_CHIP: Record<PartnerActivity, string> = {
  active: "bg-emerald-100 text-emerald-700",
  dormant: "bg-amber-100 text-amber-700",
  suspended: "bg-rose-100 text-rose-700",
};

export function ChannelPartnersListPage() {
  const [tab, setTab] = useState<string>("all");   // all / <tier code> / suspended
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<SortKey>("health");

  const boardQ = useQuery({ queryKey: ["b2b", "board"], queryFn: () => partnersApi.board() });
  const tiersQ = useQuery({ queryKey: ["b2b", "tiers"], queryFn: () => partnersApi.listTiers() });
  const d = boardQ.data;
  const tiers = (tiersQ.data ?? []).filter((t) => t.is_active);

  const rows = useMemo(() => {
    let xs: BoardRow[] = d?.rows ?? [];
    if (tab === "suspended") xs = xs.filter((r) => r.activity === "suspended");
    else if (tab !== "all") xs = xs.filter((r) => r.tier === tab);
    if (q) xs = xs.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()) || (r.code ?? "").toLowerCase().includes(q.trim().toLowerCase()));   // IDFILL-1 · 名称 / 编号都能搜
    const rank = { suspended: 0, dormant: 1, active: 2 } as Record<PartnerActivity, number>;
    return [...xs].sort((a, b) =>
      sort === "volume" ? b.order_count - a.order_count : rank[a.activity] - rank[b.activity] || b.order_count - a.order_count,
    );
  }, [d, tab, q, sort]);

  if (boardQ.isLoading) return <LoadingState />;
  if (boardQ.isError) return (
    <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-slate-500">
      <span>同业客户加载失败</span>
      <button onClick={() => boardQ.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>
    </div>
  );
  if (!d) return <LoadingState />;

  const countOf = (code: string) => d.tier_counts.find((t) => t.code === code)?.count ?? 0;
  const tierSummary = d.tier_counts.map((t) => `${t.name} ${t.count}`).join(" + ");

  return (
    <PageShell
      title="🤝 同业 B2B · 客户管理"
      subtitle={`${d.total} 家同业客户 · ${tiers.length} 级分级 · 健康度打分`}
      actions={
        <div className="flex gap-2">
          <Link to="/admin/channel-partners/workbench" className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-medium text-slate-700 hover:bg-slate-50"><Briefcase className="h-3.5 w-3.5" /> 我的工作台</Link>
          <Can perm="channel_partners.settlements.view"><Link to="/admin/channel-partners/settlements" className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-medium text-slate-700 hover:bg-slate-50"><Wallet className="h-3.5 w-3.5" /> 对账结算</Link></Can>
          <Can perm="channel_partners.partners.create"><Link to="/admin/channel-partners/new" className="flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white hover:bg-blue-700"><Plus className="h-3.5 w-3.5" /> 新增 B2B 客户</Link></Can>
        </div>
      }
    >
      <WizardDraftsBar kind="channel_partner" />   {/* WIZDRAFT-2 · 没做完的新建同行客户（有草稿才出现） */}
      {/* 4 KPI */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="客户总数" value={String(d.total)} sub={tierSummary || "—"} />
        <Kpi label="本月新增" value={String(d.month_new)} sub="新入网客户" />
        <Kpi label="临时额度待审" value={String(d.pending_credits)} sub="🔴 老板需处理" tone="danger" />
        <Kpi label="本月 B2B 流水" value={formatCents(d.month_revenue_cents)} sub={`占总营收 ${d.revenue_pct}%`} tone="accent" />
      </div>

      {/* 动态分级 baseline 说明卡 */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="text-[14px] font-semibold text-slate-900">📋 客户分级（{tiers.length} 级 · 动态可配）</div>
            <div className="text-[11px] text-slate-500">系统按业务量自动定级 · 单客户可在详情手动 override（走老板审批）</div>
          </div>
          <Link to="/admin/pricing/tiers" className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-medium text-slate-700 hover:bg-slate-50">
            <Settings2 className="h-3.5 w-3.5" /> 管理分级
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {tiers.map((t) => (
            <div key={t.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between">
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tierChip(t.code)}`}>{t.name}</span>
                <span className="text-[13px] font-bold text-slate-800">{countOf(t.code)} 家</span>
              </div>
              <div className="mt-1.5 text-[11px] text-slate-500">月单 ≥ {t.min_monthly_orders} 或 年流水 ≥ ${(t.min_annual_gmv_cents / 100 / 1000).toFixed(0)}k</div>
              <div className="text-[11px] text-slate-400">信用 {formatCents(t.credit_min_cents)}–{formatCents(t.credit_max_cents)} · markup {t.baseline_markup_pct}%</div>
            </div>
          ))}
        </div>
      </section>

      {/* 分层 tab（动态）*/}
      <div className="flex flex-wrap gap-2">
        <TierTab label="全部" count={d.total} active={tab === "all"} onClick={() => setTab("all")} />
        {tiers.map((t) => (
          <TierTab key={t.code} label={t.name} count={countOf(t.code)} active={tab === t.code} onClick={() => setTab(t.code)} />
        ))}
        <TierTab label="暂停" count={d.suspended} active={tab === "suspended"} onClick={() => setTab("suspended")} />
      </div>

      {/* 搜索 + 排序 */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3">
          <Search className="h-3.5 w-3.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜索 名称 / 编号" data-testid="partners-search" className="bg-transparent text-[13px] outline-none" />
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-[13px]">
          <option value="health">健康度排序</option>
          <option value="volume">月均单量排序</option>
        </select>
        <span className="text-[12px] text-slate-500">共 {d.total} 家 · 显示 {rows.length}</span>
      </div>

      {/* 客户行 */}
      {rows.length === 0 ? (
        <EmptyState message="无符合条件的客户" />
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((r) => <PartnerRow key={r.id} r={r} />)}
        </div>
      )}
    </PageShell>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: "danger" | "accent" }) {
  const color = tone === "danger" ? "#EF4444" : tone === "accent" ? "rgb(var(--c-blue-600))" : "#0F172A";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[11px] font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-[22px] font-bold leading-tight" style={{ color }}>{value}</div>
      <div className="mt-0.5 text-[10px] text-slate-400">{sub}</div>
    </div>
  );
}

function TierTab({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className={`flex h-9 items-center gap-1.5 rounded-full border px-3 text-[12px] font-medium transition ${active ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50"}`}>
      <span>{label}</span>
      <span className={`rounded-full px-1.5 text-[11px] ${active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"}`}>{count}</span>
    </button>
  );
}

function PartnerRow({ r }: { r: BoardRow }) {
  const last = r.last_order_days == null ? "无订单" : r.last_order_days === 0 ? "今日" : r.last_order_days === 1 ? "昨日" : `${r.last_order_days} 天前`;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[14px] font-bold text-white ${tierAvatar(r.activity)}`}>{r.name.slice(0, 1)}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[13px] font-semibold text-slate-900">{r.name}</span>
          {r.code && <span data-testid={`partner-code-${r.code}`} className="shrink-0 font-mono text-[11px] text-slate-400">{r.code}</span>}
          <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${tierChip(r.tier)}`}>{r.tier_name}</span>
        </div>
        <div className="text-[11px] text-slate-500">上次：{last}</div>
      </div>
      <div className="hidden text-center sm:block"><div className="text-[13px] font-bold text-slate-800">{r.order_count}</div><div className="text-[10px] text-slate-400">单</div></div>
      <div className="hidden w-28 text-right md:block"><div className="text-[12px] font-medium text-slate-700">{formatCents(r.used_cents)} / {formatCents(r.limit_cents)}</div><div className="text-[10px] text-slate-400">月均 / 额度</div></div>
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${ACT_CHIP[r.activity]}`}>{ACTIVITY_LABEL[r.activity]}</span>
      <Link to={`/admin/channel-partners/${r.id}`} className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-medium text-slate-700 hover:bg-slate-50">查看</Link>
    </div>
  );
}
